import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Descriptions, Drawer, Form, Modal, Space, Spin } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCustomerServiceCommissionSetup,
    updateCustomerServiceCommissionSetup,
} from '@/redux/actions/customerServicesActions';
import CustomerServiceConditionFields from './CustomerServiceConditionFields';
import CustomerServiceCommissionAllocationFields from './CustomerServiceCommissionAllocationFields';
import { normalizeAllocationConfiguration } from './customerServiceAllocationHelpers';
import { buildConditionValues } from './serviceConditionHelpers';
import { styles } from './styles';
import {
    appendMissingAllocationModes,
    cloneCustomerServiceFormData,
} from './customerServiceFormStateHelpers';

function CustomerServiceCommissionSetupForm({ visible, customerServiceId, onCancel, onSaved, readOnly = false }) {
    const dispatch = useDispatch();
    const doctors = useSelector(state => state.config.doctors) || [];
    const sales = useSelector(state => state.config.sales) || [];
    const recipientRoles = useSelector(state => state.config.commission_recipient_roles) || [];
    const allocationRoles = useMemo(() => recipientRoles.filter(role => (
        role.assignment_source === 'DOCTOR_ASSIGNMENT' || role.assignment_source === 'SALES_ASSIGNMENT'
    )), [recipientRoles]);
    const allocationRoleKeys = useMemo(() => allocationRoles.map(role => role.role_key), [allocationRoles]);
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const loadRequestRef = useRef(0);
    const allocationRoleKeysRef = useRef(allocationRoleKeys);
    allocationRoleKeysRef.current = allocationRoleKeys;

    useEffect(() => {
        const requestId = loadRequestRef.current + 1;
        loadRequestRef.current = requestId;

        if (!visible || !customerServiceId) {
            setItem(null);
            setLoading(false);
            setSaving(false);
            return undefined;
        }

        let active = true;
        setItem(null);
        setLoading(true);

        dispatch(getCustomerServiceCommissionSetup(customerServiceId))
            .then((responseData) => {
                if (!active || loadRequestRef.current !== requestId) return;
                const data = cloneCustomerServiceFormData(responseData);
                const allocation = normalizeAllocationConfiguration(
                    data,
                    allocationRoleKeysRef.current
                );
                setItem({
                    ...data,
                    name: data?.service?.name || '',
                    service_group_name: data?.service?.service_group?.name || '',
                    doctors: (data.commission_doctor_ids || []).map(Number),
                    sales: (data.commission_sales_ids || []).map(Number),
                    condition_values: buildConditionValues(data.condition_values || [], data.condition_values || []),
                    allocation_modes: allocation.allocation_modes,
                    allocation_agreements: allocation.allocation_agreements,
                    work_units: allocation.work_units,
                });
            })
            .catch(() => {
                // Redux Action đã hiển thị lỗi theo response backend.
            })
            .finally(() => {
                if (active && loadRequestRef.current === requestId) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [visible, customerServiceId, dispatch]);

    useEffect(() => {
        if (!visible || !allocationRoleKeys.length) return;
        setItem(current => {
            if (!current) return current;
            const allocationModes = appendMissingAllocationModes(
                current.allocation_modes,
                allocationRoleKeys
            );
            return allocationModes === current.allocation_modes
                ? current
                : { ...current, allocation_modes: allocationModes };
        });
    }, [visible, allocationRoleKeys, item]);

    const onChangeItem = useCallback((name, value) => {
        setItem(current => {
            if (!current) return current;
            if (name === 'commission_allocation_data') {
                return {
                    ...current,
                    allocation_modes: value?.allocation_modes || [],
                    allocation_agreements: value?.allocation_agreements || [],
                    work_units: value?.work_units || [],
                };
            }
            return { ...current, [name]: value };
        });
    }, []);

    const validateAllocation = useCallback(() => {
        for (const roleKey of allocationRoleKeys) {
            const mode = (item?.allocation_modes || []).find(row => row.role_key === roleKey)?.allocation_mode || 'equal_split';
            if (mode === 'agreement_percent') {
                const agreements = (item?.allocation_agreements || []).filter(row => row.role_key === roleKey);
                const role = allocationRoles.find(row => row.role_key === roleKey);
                const expectedIds = role?.assignment_source === 'SALES_ASSIGNMENT'
                    ? (item?.sales || []) : (item?.doctors || []);
                const rows = agreements.length === 1 ? (agreements[0].items || []) : [];
                const actualIds = rows.filter(row => Number(row.percent) > 0).map(row => Number(row.employee_id));
                const total = rows.reduce((sum, row) => sum + (Number(row.percent) || 0), 0);
                const exactRecipients = expectedIds.length === actualIds.length
                    && expectedIds.every(id => actualIds.includes(Number(id)));
                if (agreements.length !== 1 || !exactRecipients || Math.abs(total - 100) >= 0.0001) return false;
            }
            if (mode === 'work_unit' && !(item?.work_units || []).some(row => row.role_key === roleKey && Number(row.employee_id) && Number(row.unit_value) > 0)) return false;
        }
        return true;
    }, [item, allocationRoleKeys, allocationRoles]);

    const handleSave = useCallback(async () => {
        const missingDate = (item?.condition_values || []).find(condition => condition.is_satisfied && condition.require_date && !condition.occurred_at);
        if (missingDate) {
            Modal.warning({ title: 'Thiếu ngày đáp ứng', content: 'Vui lòng nhập ngày đáp ứng cho các điều kiện đã chọn.' });
            return;
        }
        if (!validateAllocation()) {
            Modal.warning({ title: 'Phân bổ chưa hợp lệ', content: 'Tỷ lệ phải đủ 100%, gồm đúng các nhân viên hiện thuộc role; phần việc phải có người hợp lệ và trọng số lớn hơn 0.' });
            return;
        }
        setSaving(true);
        try {
            await dispatch(updateCustomerServiceCommissionSetup(customerServiceId, {
                condition_values: (item.condition_values || []).filter(row => !row.read_only).map(row => ({
                    service_condition_id: Number(row.service_condition_id),
                    is_satisfied: !!row.is_satisfied,
                    value_number: row.value_number ?? row.metadata?.value_number ?? null,
                    occurred_at: row.occurred_at || null,
                    note: row.note || null,
                    metadata: row.metadata || null,
                })),
                allocation_modes: (item.allocation_modes || []).filter(row => row.id || row.is_manual_override).map(row => ({
                    id: row.id,
                    role_key: row.role_key,
                    allocation_mode: row.allocation_mode || 'equal_split',
                    note: row.note || null,
                })),
                allocation_agreements: item.allocation_agreements || [],
                work_units: item.work_units || [],
            }));
            if (onSaved) await onSaved(customerServiceId);
            onCancel();
        } finally {
            setSaving(false);
        }
    }, [item, validateAllocation, dispatch, customerServiceId, onSaved, onCancel]);

    const handleClose = useCallback(() => {
        loadRequestRef.current += 1;
        setItem(null);
        setLoading(false);
        setSaving(false);
        onCancel();
    }, [onCancel]);

    return (
        <Drawer
            visible={visible}
            title="Thiết lập hoa hồng dịch vụ"
            onClose={handleClose}
            maskClosable={false}
            width={isMobile ? '100%' : '65%'}
            footer={<div style={{ textAlign: 'right' }}><Space><Button size='large' onClick={handleClose}>Đóng</Button>{!readOnly && <Button size='large' type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Lưu thiết lập</Button>}</Space></div>}
        >
            <Spin spinning={loading}>
                {item ? <Form layout="vertical">
                    <Descriptions bordered size="small" column={isMobile ? 1 : 2} style={{ marginBottom: 16 }}>
                        <Descriptions.Item label="Khách hàng">{item?.customer?.full_name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Dịch vụ">{item?.service?.name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Ngày chốt">{item?.selected_at || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Giá trị dịch vụ">{Number(item?.amount || 0).toLocaleString('vi-VN')} đ</Descriptions.Item>
                    </Descriptions>
                    <Alert type="info" message="Đối tượng phân bổ chỉ lấy từ nhân sự đã được ghi nhận tại Dịch vụ chốt; form này không tạo thêm bác sĩ." style={{ marginBottom: 16 }} />
                    <CustomerServiceConditionFields item={item} onChangeItem={onChangeItem} />
                    <CustomerServiceCommissionAllocationFields
                        item={item}
                        doctors={doctors}
                        sales={sales}
                        recipientRoles={allocationRoles}
                        styles={styles}
                        onChangeItem={onChangeItem}
                    />
                </Form> : null}
            </Spin>
        </Drawer>
    );
}

export default CustomerServiceCommissionSetupForm;
